/**
 * Additional context section (per-tenant `AgentTenantConfig.additionalContext`): a single textarea
 * plus Save and History Sheet, where the upsert mutation creates the row if it does not exist. The
 * Reason input is the shared `<Input>` + `<Label>` pair used by the other four Reason widgets on
 * this page. See history: SIM-1502, SIM-1676.
 */
import { Button, Input, Label, QueryView, Textarea } from "@simplicity/ui";
import { useState } from "react";
import { notifyMutationError, notifyWarnings } from "./notify";
import { RevisionViewer } from "./RevisionViewer";
import type { AgentTenantConfig } from "./types";
import { useTenantConfig, useUpsertTenantConfig } from "./useDottieContext";

interface AdditionalContextSectionProps {
  tenantId: string;
}

function ContextEditor({
  tenantId,
  initial,
  onOpenHistory,
}: {
  tenantId: string;
  initial: AgentTenantConfig | null;
  onOpenHistory: () => void;
}) {
  const [draft, setDraft] = useState<string>(initial?.additionalContext ?? "");
  const [reason, setReason] = useState<string>("");
  const [snapKey, setSnapKey] = useState<string>(initial?.updatedAt ?? "");
  const incomingKey = initial?.updatedAt ?? "";
  if (snapKey !== incomingKey) {
    setSnapKey(incomingKey);
    setDraft(initial?.additionalContext ?? "");
  }
  const upsert = useUpsertTenantConfig(tenantId);

  const handleSave = () => {
    upsert.mutate(
      {
        tenantId,
        additionalContext: draft.trim() === "" ? null : draft,
        reason: reason || undefined,
      },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          // SIM-1680 B5: Reason persists across save failures (sheet/form
          // state intact) and clears on success — matching the entity sheets
          // whose form remounts on next open.
          setReason("");
        },
        onError: (err) => notifyMutationError("Save additional context", err),
      },
    );
  };

  return (
    <div className="space-y-3">
      <Textarea
        aria-label="Additional context"
        rows={12}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="No additional context configured for this tenant."
      />
      <div className="flex items-end gap-2">
        <div className="w-full max-w-sm space-y-1">
          <Label htmlFor="additional-context-reason">Reason (optional)</Label>
          <Input
            id="additional-context-reason"
            aria-label="Reason"
            placeholder="Why are you making this change?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <Button onClick={handleSave} disabled={upsert.isPending}>
          Save
        </Button>
        <Button variant="outline" onClick={onOpenHistory}>
          History
        </Button>
      </div>
    </div>
  );
}

export function AdditionalContextSection({ tenantId }: AdditionalContextSectionProps) {
  const configQuery = useTenantConfig(tenantId);
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold text-foreground">Additional context</h2>

      <QueryView
        query={configQuery}
        error={(err) => (
          <p className="text-sm text-destructive" role="alert">
            Failed to load additional context: {err.message}
          </p>
        )}
      >
        {(data) => (
          <ContextEditor
            tenantId={tenantId}
            initial={data}
            onOpenHistory={() => setHistoryOpen(true)}
          />
        )}
      </QueryView>

      <RevisionViewer
        entityType="tenantConfig"
        id={tenantId}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        title="Additional context history"
      />
    </section>
  );
}
